'use client';

import React from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import { JobDetail } from '~/types/job-detail';
import { SpeechBubble } from '~/components/jobs/speech-bubble';
import CompanyInterview from '~/components/jobs/company-interview';

interface JobRequirementsProps {
  job: JobDetail;
  isSignedIn: boolean;
  isMobile: boolean;
}

const formatText = (text: string) =>
  text ? text.replace(/\r\n|\r|\n/g, '<br>') : '';

export function JobRequirements({
  job,
  isSignedIn,
  isMobile,
}: JobRequirementsProps) {
  const mobile = isMobile;
  const pc = !mobile;

  return (
    <div className="p-job-detail__information-table">
      {/* 仕事内容 */}
      <div className="p-job-detail__information-table-row">
        {mobile ? (
          <div className="p-job-detail__information-table-header">
            <h3
              id="description"
              className={classNames(
                'p-job-detail__heading',
                'u-flex',
                'u-font-bold-imp',
                '-items-center',
                'u-pl6',
              )}
            >
              <Image
                src="/nextjs/images/icons/ic_bag_white.svg"
                alt="bag white"
                width={16}
                height={16}
                style={{ maxWidth: 'none', height: '16px' }}
                className="u-mr4"
              />
              仕事内容
            </h3>
          </div>
        ) : (
          <div className="p-job-detail__information-table-header u-flex">
            <Image
              src="/nextjs/images/icons/ic_bag.svg"
              alt="bag"
              width={20}
              height={20}
              style={{ maxWidth: 'none', height: '20px' }}
              className="u-mr6 u-mt1"
            />
            <h3 id="description" className="p-job-detail__heading">
              仕事内容
            </h3>
          </div>
        )}
        <div className="p-job-detail__information-table-content">
          {job.category_info?.category_detail && (
            <div
              dangerouslySetInnerHTML={{
                __html: formatText(job.category_info.category_detail),
              }}
            />
          )}
          {job.working_style_info?.decorate?.description && (
            <div
              dangerouslySetInnerHTML={{
                __html: formatText(job.working_style_info?.decorate?.description),
              }}
            />
          )}
          {job.category_info?.work_detail && (
            <div
              dangerouslySetInnerHTML={{
                __html: formatText(job.category_info.work_detail),
              }}
            />
          )}
          {!job.category_info?.category_detail &&
            !job.working_style_info?.decorate?.description &&
            !job.category_info?.work_detail && (
              <div>※ 詳細は面談時にお伝えいたします。</div>
            )}
        </div>
      </div>

      {/* 休日・休暇 */}
      {job.employment_info.holiday_detail && (
        <div
          className={classNames('p-job-detail__information-table-row', {
            'u-pt70': job.employment_info.holiday_feature && mobile,
            'u-pos-rel': job.employment_info.holiday_feature,
          })}
        >
          {job.employment_info.holiday_feature && (
            <SpeechBubble text="この求人は、<b>土日祝日が休み</b>になります。ワークライフバランスを重視する方におすすめの求人です。" />
          )}
          {mobile ? (
            <div className="p-job-detail__information-table-header">
              <h3
                id="holiday_detail"
                className={classNames(
                  'p-job-detail__heading',
                  'u-flex',
                  'u-font-bold-imp',
                  '-items-center',
                  'u-pl6',
                )}
              >
                <Image
                  src="/nextjs/images/icons/ic_calendar_white.svg"
                  alt="calendar white"
                  width={16}
                  height={16}
                  style={{ maxWidth: 'none', height: '16px' }}
                  className="u-mr4"
                />
                休日・休暇
              </h3>
            </div>
          ) : (
            <div
              className={classNames(
                'p-job-detail__information-table-header',
                'u-flex',
                {
                  'u-pt80': job.employment_info.holiday_feature && pc,
                },
              )}
            >
              <Image
                src="/nextjs/images/icons/ic_calendar.svg"
                alt="calendar"
                width={20}
                height={20}
                style={{ maxWidth: 'none', height: '20px' }}
                className="u-mr6 u-mt2"
              />
              <h3 id="holiday_detail" className="p-job-detail__heading">
                休日・休暇
              </h3>
            </div>
          )}
          <div
            className={classNames('p-job-detail__information-table-content', {
              'u-pt80': job.employment_info.holiday_feature && pc,
            })}
          >
            <div style={{ whiteSpace: 'pre-line' }}>
              {job.employment_info.holiday_detail}
            </div>
          </div>
        </div>
      )}

      {/* 応募条件 */}
      {(job.license_skill_info.license_detail ||
        job.license_skill_info.license_note ||
        job.license_skill_info.optional_license_detail ||
        job.license_skill_info.optional_license_note ||
        job.license_skill_info.experience_detail ||
        job.license_skill_info.required_skill_detail ||
        job.license_skill_info.desired_skill ||
        job.license_skill_info.education_detail) && (
        <div
          className={classNames('p-job-detail__information-table-row', {
            'u-pt70': job.license_skill_info.license_feature && mobile,
            'u-pos-rel': job.license_skill_info.license_feature,
          })}
        >
          {job.license_skill_info.license_feature && (
            <SpeechBubble text="この求人は、<b>資格の取得支援</b>があります。仕事をしながら資格を取得したい方におすすめの求人です。" />
          )}
          {mobile ? (
            <div
              className={classNames('p-job-detail__information-table-header', {
                'u-pt80': job.license_skill_info.license_feature && pc,
              })}
            >
              <h3
                id="requirements"
                className={classNames(
                  'p-job-detail__heading',
                  'u-flex',
                  'u-font-bold-imp',
                  '-items-center',
                  'u-pl6',
                )}
              >
                <Image
                  src="/nextjs/images/icons/ic_license_white.svg"
                  alt="license white"
                  width={16}
                  height={16}
                  className="u-mr4"
                  style={{ maxWidth: 'none', height: '16px' }}
                />
                応募条件
              </h3>
            </div>
          ) : (
            <div
              className={classNames(
                'p-job-detail__information-table-header',
                'u-flex',
                {
                  'u-pt80': job.license_skill_info.license_feature && pc,
                },
              )}
            >
              <Image
                src="/nextjs/images/icons/ic_license.svg"
                alt="license"
                width={20}
                height={20}
                className="u-mr6 u-mt2"
                style={{ maxWidth: 'none', height: '20px' }}
              />
              <h3 id="requirements" className="p-job-detail__heading">
                応募条件
              </h3>
            </div>
          )}
          <div
            className={classNames('p-job-detail__information-table-content', {
              'u-pt80': job.license_skill_info.license_feature && pc,
            })}
          >
            {job.license_skill_info.license_detail &&
              job.license_skill_info.license_detail !== '#N/A' && (
                <>
                  <p
                    className={classNames(
                      'p-job-detail__sub-heading',
                      '-h3',
                      'u-color-main',
                      'u-mb6',
                      'u-pl6',
                      'u-border-l-solid-3',
                      { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                    )}
                  >
                    資格・免許
                  </p>
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {job.license_skill_info.license_detail}
                  </div>
                </>
              )}
            {job.license_skill_info.license_note &&
              job.license_skill_info.license_note !== '#N/A' && (
                <>
                  <p
                    className={classNames(
                      'p-job-detail__sub-heading',
                      '-h3',
                      'u-color-main',
                      'u-mb6',
                      'u-pl6',
                      'u-border-l-solid-3',
                      { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                    )}
                  >
                    資格備考
                  </p>
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {job.license_skill_info.license_note}
                  </div>
                </>
              )}
            {job.license_skill_info.optional_license_detail &&
              job.license_skill_info.optional_license_detail !== '#N/A' && (
                <>
                  <p
                    className={classNames(
                      'p-job-detail__sub-heading',
                      '-h3',
                      'u-color-main',
                      'u-mb6',
                      'u-pl6',
                      'u-border-l-solid-3',
                      { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                    )}
                  >
                    望ましい免許・資格
                  </p>
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {job.license_skill_info.optional_license_detail}
                  </div>
                </>
              )}
            {(job.license_skill_info.experience_detail ||
              job.license_skill_info.required_skill_detail ||
              job.license_skill_info.desired_skill) && (
              <>
                <p
                  className={classNames(
                    'p-job-detail__sub-heading',
                    '-h3',
                    'u-color-main',
                    'u-mb6',
                    'u-pl6',
                    'u-border-l-solid-3',
                    { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                  )}
                >
                  経験
                </p>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.license_skill_info.experience_detail}
                </div>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.license_skill_info.required_skill_detail}
                </div>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.license_skill_info.desired_skill}
                </div>
              </>
            )}
            {job.license_skill_info.education_detail && (
              <>
                <p
                  className={classNames(
                    'p-job-detail__sub-heading',
                    '-h3',
                    'u-color-main',
                    'u-mb6',
                    'u-pl6',
                    'u-border-l-solid-3',
                    { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                  )}
                >
                  学歴
                </p>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.license_skill_info.education_detail}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 求める人物像 */}
      {job.category_info.desired_personality && (
        <div className="p-job-detail__information-table-row">
          {mobile ? (
            <div className="p-job-detail__information-table-header">
              <h3
                id="desired_personality"
                className={classNames(
                  'p-job-detail__heading',
                  'u-flex',
                  'u-font-bold-imp',
                  '-items-center',
                  'u-pl6',
                )}
              >
                <Image
                  src="/nextjs/images/icons/ic_demanded_human_white.svg"
                  alt="desired personality white"
                  width={16}
                  height={16}
                  className="u-mr4"
                  style={{ maxWidth: 'none', height: '16px' }}
                />
                求める人物像
              </h3>
            </div>
          ) : (
            <div className="p-job-detail__information-table-header u-flex">
              <Image
                src="/nextjs/images/icons/ic_demanded_human.svg"
                alt="desired personality"
                width={20}
                height={20}
                className="u-mr6 u-mt2"
                style={{ maxWidth: 'none', height: '20px' }}
              />
              <h3 id="desired_personality" className="p-job-detail__heading">
                求める人物像
              </h3>
            </div>
          )}
          <div className="p-job-detail__information-table-content">
            <div style={{ whiteSpace: 'pre-line' }}>
              {job.category_info.desired_personality}
            </div>
          </div>
        </div>
      )}

      {/* 雇用形態 */}
      <div className="p-job-detail__information-table-row">
        {mobile ? (
          <div className="p-job-detail__information-table-header">
            <h3
              id="contract_detail"
              className={classNames(
                'p-job-detail__heading',
                'u-flex',
                'u-font-bold-imp',
                '-items-center',
                'u-pl6',
              )}
            >
              <Image
                src="/nextjs/images/icons/ic_employment_status_human_white.svg"
                alt="employment status white"
                width={16}
                height={16}
                className="u-mr4"
                style={{ maxWidth: 'none', height: '16px' }}
              />
              雇用形態
            </h3>
          </div>
        ) : (
          <div className="p-job-detail__information-table-header u-flex">
            <Image
              src="/nextjs/images/icons/ic_employment_status_human.svg"
              alt="employment status"
              width={20}
              height={20}
              className="u-mr6 u-mt2"
              style={{ maxWidth: 'none', height: '20px' }}
            />
            <h3 id="contract_detail" className="p-job-detail__heading">
              雇用形態
            </h3>
          </div>
        )}
        <div className="p-job-detail__information-table-content">
          {job.employment_info.contract_detail ? (
            <div style={{ whiteSpace: 'pre-line' }}>
              {job.employment_info.contract_detail}
            </div>
          ) : (
            <div>※ 詳細は面談時にお伝えいたします。</div>
          )}
        </div>
      </div>

      {/* 勤務地 */}
      {job.service_type === '自社提案' && !job.display_company_info ? (
        <div className="p-job-detail__information-table-row">
          {mobile ? (
            <div className="p-job-detail__information-table-header">
              <h3
                id="work_location"
                className={classNames(
                  'p-job-detail__heading',
                  'u-flex',
                  'u-font-bold-imp',
                  '-items-center',
                  'u-pl6',
                )}
              >
                <Image
                  src="/nextjs/images/icons/ic_location_white.svg"
                  alt="location white"
                  width={16}
                  height={16}
                  className="u-mr4"
                  style={{ maxWidth: 'none', height: '16px' }}
                />
                勤務地
              </h3>
            </div>
          ) : (
            <div className="p-job-detail__information-table-header u-flex">
              <Image
                src="/nextjs/images/icons/ic_location.svg"
                alt="location"
                width={20}
                height={20}
                className="u-mr6 u-mt2"
                style={{ maxWidth: 'none', height: '20px' }}
              />
              <h3 id="work_location" className="p-job-detail__heading">
                勤務地
              </h3>
            </div>
          )}
          <div className="p-job-detail__information-table-content">
            {job.location_info?.prefecture_and_city_name}
          </div>
        </div>
      ) : (
        <>
          {job.location_info?.display_address_google_map && (
            <div className="p-job-detail__information-table-row">
              {mobile ? (
                <div className="p-job-detail__information-table-header">
                  <h3
                    id="address_google_map"
                    className={classNames(
                      'p-job-detail__heading',
                      'u-flex',
                      'u-font-bold-imp',
                      '-items-center',
                      'u-pl6',
                    )}
                  >
                    <Image
                      src="/nextjs/images/icons/ic_location_white.svg"
                      alt="location white"
                      width={16}
                      height={16}
                      className="u-mr4"
                      style={{ maxWidth: 'none', height: '16px' }}
                    />
                    勤務地
                  </h3>
                </div>
              ) : (
                <div className="p-job-detail__information-table-header u-flex">
                  <Image
                    src="/nextjs/images/icons/ic_location.svg"
                    alt="location"
                    width={20}
                    height={20}
                    className="u-mr6 u-mt2"
                    style={{ maxWidth: 'none', height: '20px' }}
                  />
                  <h3 id="address_google_map" className="p-job-detail__heading">
                    勤務地
                  </h3>
                </div>
              )}
              <div className="p-job-detail__information-table-content">
                {job.location_info?.address_google_map}
              </div>
            </div>
          )}
          <div className="p-job-detail__information-table-row">
            {mobile ? (
              <div className="p-job-detail__information-table-header">
                <h3
                  id="work_location"
                  className={classNames(
                    'p-job-detail__heading',
                    'u-flex',
                    'u-font-bold-imp',
                    '-items-center',
                    'u-pl6',
                  )}
                >
                  <Image
                    src="/nextjs/images/icons/ic_location_white.svg"
                    alt="location white"
                    width={16}
                    height={16}
                    className="u-mr4"
                    style={{ maxWidth: 'none', height: '16px' }}
                  />
                  {job.location_info?.display_address_google_map
                    ? '勤務地詳細'
                    : '勤務地'}
                </h3>
              </div>
            ) : (
              <div className="p-job-detail__information-table-header u-flex">
                <Image
                  src="/nextjs/images/icons/ic_location.svg"
                  alt="location"
                  width={20}
                  height={20}
                  className="u-mr6 u-mt2"
                  style={{ maxWidth: 'none', height: '20px' }}
                />
                <h3 id="work_location" className="p-job-detail__heading">
                  {job.location_info?.display_address_google_map
                    ? '勤務地詳細'
                    : '勤務地'}
                </h3>
              </div>
            )}
            <div className="p-job-detail__information-table-content">
              {job.location_info?.address && (
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.location_info.address}
                </div>
              )}
              {(job.location_info?.commute_to_work ||
                job.location_info?.commute_to_work_note) && (
                <>
                  <p
                    className={classNames(
                      'p-job-detail__sub-heading',
                      '-h3',
                      'u-color-main',
                      'u-mb6',
                      'u-pl6',
                      'u-border-l-solid-3',
                      { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                    )}
                  >
                    通勤手段
                  </p>
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {job.location_info?.commute_to_work}
                  </div>
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {job.location_info?.commute_to_work_note}
                  </div>
                </>
              )}
              {(job.employment_info?.reloacation ||
                job.employment_info?.reloacation_trip) && (
                <>
                  <p
                    className={classNames(
                      'p-job-detail__sub-heading',
                      '-h3',
                      'u-color-main',
                      'u-mb6',
                      'u-pl6',
                      'u-border-l-solid-3',
                      { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                    )}
                  >
                    転勤
                  </p>
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {job.employment_info.reloacation}
                  </div>
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {job.employment_info.reloacation_trip}
                  </div>
                </>
              )}
              {(job.employment_info.business_trip ||
                job.employment_info.business_trip_note) && (
                <>
                  <p
                    className={classNames(
                      'p-job-detail__sub-heading',
                      '-h3',
                      'u-color-main',
                      'u-mb6',
                      'u-pl6',
                      'u-border-l-solid-3',
                      { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                    )}
                  >
                    出張
                  </p>
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {job.employment_info.business_trip}
                  </div>
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {job.employment_info.business_trip_note}
                  </div>
                </>
              )}
              {!job.location_info.address &&
                !job.location_info.commute_to_work &&
                !job.location_info.commute_to_work_note &&
                !job.employment_info.reloacation &&
                !job.employment_info.reloacation_trip &&
                !job.employment_info.business_trip &&
                !job.employment_info.business_trip_note && (
                  <div>※ 詳細は面談時にお伝えいたします。</div>
                )}
            </div>
          </div>
        </>
      )}

      {/* 給与 */}
      {(job.employment_info.salary_detail ||
        job.employment_info.bonus ||
        job.employment_info.annual_salary_min ||
        job.employment_info.annual_salary_max ||
        job.employment_info.annual_salary_note) && (
        <div className="p-job-detail__information-table-row">
          {mobile ? (
            <div className="p-job-detail__information-table-header">
              <h3
                id="salary"
                className={classNames(
                  'p-job-detail__heading',
                  'u-flex',
                  'u-font-bold-imp',
                  '-items-center',
                  'u-pl6',
                )}
              >
                <Image
                  src="/nextjs/images/icons/ic_money_white.svg"
                  alt="money white"
                  width={16}
                  height={16}
                  className="u-mr4"
                  style={{ maxWidth: 'none', height: '16px' }}
                />
                給与
              </h3>
            </div>
          ) : (
            <div className="p-job-detail__information-table-header u-flex">
              <Image
                src="/nextjs/images/icons/ic_money.svg"
                alt="money"
                width={20}
                height={20}
                className="u-mr6 u-mt2"
                style={{ maxWidth: 'none', height: '20px' }}
              />
              <h3 id="salary" className="p-job-detail__heading">
                給与
              </h3>
            </div>
          )}
          <div className="p-job-detail__information-table-content">
            <div style={{ whiteSpace: 'pre-line' }}>
              {job.employment_info.salary_detail}
            </div>
            {job.employment_info.bonus && (
              <>
                <p
                  className={classNames(
                    'p-job-detail__sub-heading',
                    '-h3',
                    'u-color-main',
                    'u-mb6',
                    'u-pl6',
                    'u-border-l-solid-3',
                    { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                  )}
                >
                  賞与
                </p>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.employment_info.bonus}
                </div>
              </>
            )}
            {(job.employment_info.annual_salary_min ||
              job.employment_info.annual_salary_max ||
              job.employment_info.annual_salary_note) && (
              <>
                <p
                  className={classNames(
                    'p-job-detail__sub-heading',
                    '-h3',
                    'u-color-main',
                    'u-mb6',
                    'u-pl6',
                    'u-border-l-solid-3',
                    { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                  )}
                >
                  年収
                </p>
                {(job.employment_info.annual_salary_min ||
                  job.employment_info.annual_salary_max) && (
                  <div>
                    <p>
                      {job.employment_info.annual_salary_min &&
                        `${job.employment_info.annual_salary_min}万円 `}
                      ~
                      {job.employment_info.annual_salary_max &&
                        ` ${job.employment_info.annual_salary_max}万円`}
                    </p>
                  </div>
                )}
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.employment_info.annual_salary_note}
                </div>
              </>
            )}
            {(job.employment_info.monthly_salary_min ||
              job.employment_info.monthly_salary_max) && (
              <>
                <p
                  className={classNames(
                    'p-job-detail__sub-heading',
                    '-h3',
                    'u-color-main',
                    'u-mb6',
                    'u-pl6',
                    'u-border-l-solid-3',
                    { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                  )}
                >
                  月収
                </p>
                <div>
                  <p>
                    {job.employment_info.monthly_salary_min &&
                      `${job.employment_info.monthly_salary_min}万円 `}
                    ~
                    {job.employment_info.monthly_salary_max &&
                      ` ${job.employment_info.monthly_salary_max}万円`}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 勤務時間 */}
      {(job.working_style_info.working_time_detail ||
        job.working_style_info.overtime ||
        job.working_style_info.night_shift ||
        job.working_style_info.night_shift_note) && (
        <div
          id="job_working_time"
          className={classNames('p-job-detail__information-table-row', {
            'u-pt70': job.working_style_info.overtime_under_20_hours && mobile,
            'u-pos-rel': job.working_style_info.overtime_under_20_hours,
          })}
        >
          {job.working_style_info.overtime_under_20_hours && (
            <SpeechBubble text="この求人は、<b>残業時間</b>が<b>20時間以内</b>です。ワークライフバランスを重視される方におすすめの求人です。" />
          )}
          {mobile ? (
            <div className="p-job-detail__information-table-header">
              <h3
                id="working_time"
                className={classNames(
                  'p-job-detail__heading',
                  'u-flex',
                  'u-font-bold-imp',
                  '-items-center',
                  'u-pl6',
                )}
              >
                <Image
                  src="/nextjs/images/icons/ic_clock_white.svg"
                  alt="clock white"
                  width={16}
                  height={16}
                  className="u-mr4"
                  style={{ maxWidth: 'none', height: '16px' }}
                />
                勤務時間
              </h3>
            </div>
          ) : (
            <div
              className={classNames(
                'p-job-detail__information-table-header',
                'u-flex',
                {
                  'u-pt80':
                    job.working_style_info.overtime_under_20_hours && pc,
                },
              )}
            >
              <Image
                src="/nextjs/images/icons/ic_clock.svg"
                alt="clock"
                width={20}
                height={20}
                className="u-mr6 u-mt2"
                style={{ maxWidth: 'none', height: '20px' }}
              />
              <h3 id="working_time" className="p-job-detail__heading">
                勤務時間
              </h3>
            </div>
          )}
          <div
            className={classNames('p-job-detail__information-table-content', {
              'u-pt80': job.working_style_info.overtime_under_20_hours && pc,
            })}
          >
            {job.working_style_info.working_time_detail && (
              <div style={{ whiteSpace: 'pre-line' }}>
                {job.working_style_info.working_time_detail}
              </div>
            )}
            {job.working_style_info.overtime && (
              <>
                <p
                  className={classNames(
                    'p-job-detail__sub-heading',
                    '-h3',
                    'u-color-main',
                    'u-mb6',
                    'u-pl6',
                    'u-border-l-solid-3',
                    { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                  )}
                >
                  残業時間
                </p>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.working_style_info.overtime}
                </div>
              </>
            )}
            {(job.working_style_info.night_shift ||
              job.working_style_info.night_shift_note) && (
              <>
                <p
                  className={classNames(
                    'p-job-detail__sub-heading',
                    '-h3',
                    'u-color-main',
                    'u-mb6',
                    'u-pl6',
                    'u-border-l-solid-3',
                    { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                  )}
                >
                  夜勤
                </p>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.working_style_info.night_shift}
                </div>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.working_style_info.night_shift_note}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 処遇・福利厚生 */}
      {(job.welfare_info.allowance ||
        job.welfare_info.social_insurance ||
        job.welfare_info.welfare_detail ||
        job.welfare_info.severance_pay ||
        job.welfare_info.severance_pay_note ||
        job.welfare_info.retirement_age ||
        job.welfare_info.retirement_age_note) && (
        <div className="p-job-detail__information-table-row">
          {mobile ? (
            <div className="p-job-detail__information-table-header">
              <h3
                id="welfare_detail"
                className={classNames(
                  'p-job-detail__heading',
                  'u-flex',
                  'u-font-bold-imp',
                  '-items-center',
                  'u-pl6',
                )}
              >
                <Image
                  src="/nextjs/images/icons/ic_plus_circle_white.svg"
                  alt="plus circle white"
                  width={16}
                  height={16}
                  className="u-mr4"
                  style={{ maxWidth: 'none', height: '16px' }}
                />
                処遇・福利厚生
              </h3>
            </div>
          ) : (
            <div className="p-job-detail__information-table-header u-flex">
              <Image
                src="/nextjs/images/icons/ic_plus_circle.svg"
                alt="plus circle"
                width={20}
                height={20}
                className="u-mr6 u-mt2"
                style={{ maxWidth: 'none', height: '20px' }}
              />
              <h3 id="welfare_detail" className="p-job-detail__heading">
                処遇・福利厚生
              </h3>
            </div>
          )}
          <div className="p-job-detail__information-table-content">
            {job.welfare_info.allowance && (
              <div style={{ whiteSpace: 'pre-line' }}>
                {job.welfare_info.allowance}
              </div>
            )}
            {job.welfare_info.social_insurance && (
              <div style={{ whiteSpace: 'pre-line' }}>
                {job.welfare_info.social_insurance}
              </div>
            )}
            {job.welfare_info.welfare_detail && (
              <div style={{ whiteSpace: 'pre-line' }}>
                {job.welfare_info.welfare_detail}
              </div>
            )}
            {job.welfare_info.severance_pay && (
              <>
                <p
                  className={classNames(
                    'p-job-detail__sub-heading',
                    '-h3',
                    'u-color-main',
                    'u-mb6',
                    'u-pl6',
                    'u-border-l-solid-3',
                    { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                  )}
                >
                  退職金
                </p>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.welfare_info.severance_pay}
                </div>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.welfare_info.severance_pay_note}
                </div>
              </>
            )}
            {(job.welfare_info.retirement_age ||
              job.welfare_info.retirement_age_note) && (
              <>
                <p
                  className={classNames(
                    'p-job-detail__sub-heading',
                    '-h3',
                    'u-color-main',
                    'u-mb6',
                    'u-pl6',
                    'u-border-l-solid-3',
                    { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                  )}
                >
                  定年
                </p>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.welfare_info.retirement_age}
                </div>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.welfare_info.retirement_age_note}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 採用概要 */}
      {(job.working_style_info.background ||
        job.working_style_info.employment_process) && (
        <div className="p-job-detail__information-table-row">
          {mobile ? (
            <div className="p-job-detail__information-table-header">
              <h3
                id="recruitment"
                className={classNames(
                  'p-job-detail__heading',
                  'u-flex',
                  'u-font-bold-imp',
                  '-items-center',
                  'u-pl6',
                )}
              >
                <Image
                  src="/nextjs/images/icons/ic_signing_contract_white.svg"
                  alt="signing contract white"
                  width={16}
                  height={16}
                  className="u-mr4"
                  style={{ maxWidth: 'none', height: '16px' }}
                />
                採用概要
              </h3>
            </div>
          ) : (
            <div className="p-job-detail__information-table-header u-flex">
              <Image
                src="/nextjs/images/icons/ic_signing_contract.svg"
                alt="signing contract"
                width={20}
                height={20}
                className="u-mr6 u-mt2"
                style={{ maxWidth: 'none', height: '20px' }}
              />
              <h3 id="recruitment" className="p-job-detail__heading">
                採用概要
              </h3>
            </div>
          )}
          <div className="p-job-detail__information-table-content">
            {job.working_style_info.background && (
              <>
                <p
                  className={classNames(
                    'p-job-detail__sub-heading',
                    '-h3',
                    'u-color-main',
                    'u-mb6',
                    'u-pl6',
                    'u-border-l-solid-3',
                    { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                  )}
                >
                  募集の背景
                </p>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.working_style_info.background}
                </div>
              </>
            )}
            {job.working_style_info.employment_process && (
              <>
                <p
                  className={classNames(
                    'p-job-detail__sub-heading',
                    '-h3',
                    'u-color-main',
                    'u-mb6',
                    'u-pl6',
                    'u-border-l-solid-3',
                    { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                  )}
                >
                  採用プロセス
                </p>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {job.working_style_info.employment_process}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 勤務詳細 */}
      <div className="p-job-detail__information-table-row">
        {mobile ? (
          <div className="p-job-detail__information-table-header">
            <h3
              id="work_details"
              className={classNames(
                'p-job-detail__heading',
                'u-flex',
                'u-font-bold-imp',
                '-items-center',
                'u-pl6',
              )}
            >
              <Image
                src="/nextjs/images/icons/ic_hammer_white.svg"
                alt="hammer white"
                width={16}
                height={16}
                className="u-mr4"
                style={{ maxWidth: 'none', height: '16px' }}
              />
              勤務詳細
            </h3>
          </div>
        ) : (
          <div className="p-job-detail__information-table-header u-flex">
            <Image
              src="/nextjs/images/icons/ic_hammer.svg"
              alt="hammer"
              width={20}
              height={20}
              className="u-mr6 u-mt2"
              style={{ maxWidth: 'none', height: '20px' }}
            />
            <h3 id="work_details" className="p-job-detail__heading">
              勤務詳細
            </h3>
          </div>
        )}
        <div className="p-job-detail__information-table-content">
          {job.working_style_info.work_style && (
            <>
              <p
                className={classNames(
                  'p-job-detail__sub-heading',
                  '-h3',
                  'u-color-main',
                  'u-mb6',
                  'u-pl6',
                  'u-border-l-solid-3',
                  { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                )}
              >
                働き方
              </p>
              <div style={{ whiteSpace: 'pre-line' }}>
                {job.working_style_info.work_style}
              </div>
            </>
          )}
          {job.working_style_info.career_path && (
            <>
              <p
                className={classNames(
                  'p-job-detail__sub-heading',
                  '-h3',
                  'u-color-main',
                  'u-mb6',
                  'u-pl6',
                  'u-border-l-solid-3',
                  { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                )}
              >
                キャリアパス
              </p>
              <div style={{ whiteSpace: 'pre-line' }}>
                {job.working_style_info.career_path}
              </div>
            </>
          )}
          {job.working_style_info.obtainable_skill && (
            <>
              <p
                className={classNames(
                  'p-job-detail__sub-heading',
                  '-h3',
                  'u-color-main',
                  'u-mb6',
                  'u-pl6',
                  'u-border-l-solid-3',
                  { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
                )}
              >
                得られるスキル・経験
              </p>
              <div style={{ whiteSpace: 'pre-line' }}>
                {job.working_style_info.obtainable_skill}
              </div>
            </>
          )}
          <p
            className={classNames(
              'p-job-detail__sub-heading',
              '-h3',
              'u-color-main',
              'u-mb6',
              'u-pl6',
              'u-border-l-solid-3',
              { 'u-fs15 u-font-bold-imp': pc, 'u-fs12': mobile },
            )}
          >
            受動喫煙対策
          </p>
          {job.working_style_info.passive_smoking_status ? (
            <div style={{ whiteSpace: 'pre-line' }}>
              {job.working_style_info.passive_smoking_status}
            </div>
          ) : (
            <div>※ 詳細は面談時にお伝えいたします。</div>
          )}
        </div>
      </div>

      {/* 会社からのメッセージ */}
      {job.working_style_info.introduction && (
        <div className="p-job-detail__information-table-row">
          {mobile ? (
            <div className="p-job-detail__information-table-header">
              {job.display_company_info ? (
                <h3
                  id="messages"
                  className={classNames(
                    'p-job-detail__heading',
                    'u-flex',
                    'u-font-bold-imp',
                    '-items-center',
                    'u-pl6',
                  )}
                >
                  <Image
                    src="/nextjs/images/icons/ic_message_white.svg"
                    alt="message white"
                    width={16}
                    height={16}
                    className="u-mr4"
                    style={{ maxWidth: 'none', height: '16px' }}
                  />
                  {job.company?.name
                    ? `${job.company.name}からのメッセージ`
                    : '会社からのメッセージ'}
                </h3>
              ) : (
                <h3
                  id="messages"
                  className={classNames(
                    'p-job-detail__heading',
                    'u-flex',
                    'u-font-bold-imp',
                    '-items-center',
                    'u-pl6',
                  )}
                >
                  <Image
                    src="/nextjs/images/icons/ic_message_white.svg"
                    alt="message white"
                    width={16}
                    height={16}
                    className="u-mr4"
                    style={{ maxWidth: 'none', height: '16px' }}
                  />
                  会社からのメッセージ
                </h3>
              )}
            </div>
          ) : (
            <div className="p-job-detail__information-table-header u-flex">
              <Image
                src="/nextjs/images/icons/ic_message.svg"
                alt="message"
                width={20}
                height={20}
                className="u-mr6 u-mt2"
                style={{ maxWidth: 'none', height: '20px' }}
              />
              {job.display_company_info ? (
                <h3 id="messages" className="p-job-detail__heading">
                  {job.company?.name
                    ? `${job.company.name}からのメッセージ`
                    : '会社からのメッセージ'}
                </h3>
              ) : (
                <h3 id="messages" className="p-job-detail__heading">
                  会社からのメッセージ
                </h3>
              )}
            </div>
          )}
          <div className="p-job-detail__information-table-content">
            <div style={{ whiteSpace: 'pre-line' }}>
              {job.working_style_info.introduction
                ? job.working_style_info.introduction
                : ''}
            </div>
          </div>
        </div>
      )}

      {/* 求人の種類 */}
      {job.service_type && (
        <div className="p-job-detail__information-table-row u-dis-none">
          <div className="p-job-detail__information-table-header">
            <h3 id="service_type" className="p-job-detail__heading">
              求人の種類
            </h3>
          </div>
          <div className="p-job-detail__information-table-content">
            <div style={{ whiteSpace: 'pre-line' }}>{job.service_type}</div>
          </div>
        </div>
      )}

      {/* プラン */}
      {job.welfare_info.plan && (
        <div className="p-job-detail__information-table-row u-dis-none">
          <div className="p-job-detail__information-table-header">
            <h3 id="plan" className="p-job-detail__heading">
              プラン
            </h3>
          </div>
          <div
            id="plan_value"
            className="p-job-detail__information-table-content"
          >
            <div style={{ whiteSpace: 'pre-line' }}>
              {job.welfare_info.plan}
            </div>
          </div>
        </div>
      )}

      {/* 求人種別ID */}
      <div className="p-job-detail__information-table-row">
        {mobile ? (
          <div className="p-job-detail__information-table-header">
            <p
              id="job_id"
              className={classNames(
                'u-font-bold',
                'p-job-detail__heading',
                'u-flex',
                'u-font-bold-imp',
                '-items-center',
                'u-pl6',
              )}
            >
              <Image
                src="/nextjs/images/icons/ic_diamond_white.svg"
                alt="diamond white"
                width={16}
                height={16}
                className="u-mr4"
                style={{ maxWidth: 'none', height: '16px' }}
              />
              求人種別ID
            </p>
          </div>
        ) : (
          <div className="p-job-detail__information-table-header u-flex">
            <Image
              src="/nextjs/images/icons/ic_diamond.svg"
              alt="diamond"
              width={20}
              height={20}
              className="u-mr6 u-mt2"
              style={{ maxWidth: 'none', height: '20px' }}
            />
            <p id="job_id" className="u-font-bold p-job-detail__heading">
              求人種別ID
            </p>
          </div>
        )}
        <div className="p-job-detail__information-table-content">
          {job.service_type === '自社提案' ? '0001' : '0002'}
        </div>
      </div>

      <CompanyInterview
        answers={job.other_info.answers}
        surveyQuestions={job.other_info.questions.survey}
        normalQuestions={job.other_info.questions.normal}
        interviewComment={job.other_info.interview_comment}
        isMobile={isMobile}
      />
    </div>
  );
}
